/**
 * Lightweight browser / engine detection for developer diagnostics.
 * Not used for feature gating (capabilities are probed separately).
 */

export interface BrowserInfo {
  browser: string;
  version: string;
  engine: string;
  os: string;
  isSamsungInternet: boolean;
  isChrome: boolean;
  isFirefox: boolean;
  isSafari: boolean;
  userAgent: string;
}

export function detectBrowserInfo(
  ua: string = typeof navigator !== 'undefined' ? navigator.userAgent : '',
): BrowserInfo {
  const isSamsungInternet = /SamsungBrowser/i.test(ua);
  const isFirefox = /Firefox\//i.test(ua) && !/Seamonkey/i.test(ua);
  const isChrome =
    /Chrome\//i.test(ua) &&
    !/Edg\//i.test(ua) &&
    !/OPR\//i.test(ua) &&
    !isSamsungInternet;
  const isSafari =
    /Safari\//i.test(ua) && !/Chrome\//i.test(ua) && !isSamsungInternet;

  let browser = 'Unknown';
  let version = '';
  if (isSamsungInternet) {
    browser = 'Samsung Internet';
    version = matchVersion(ua, /SamsungBrowser\/([\d.]+)/i);
  } else if (isChrome) {
    browser = 'Chrome';
    version = matchVersion(ua, /Chrome\/([\d.]+)/i);
  } else if (isFirefox) {
    browser = 'Firefox';
    version = matchVersion(ua, /Firefox\/([\d.]+)/i);
  } else if (isSafari) {
    browser = 'Safari';
    version = matchVersion(ua, /Version\/([\d.]+)/i);
  } else if (/Edg\//i.test(ua)) {
    browser = 'Edge';
    version = matchVersion(ua, /Edg\/([\d.]+)/i);
  }

  let engine = 'Unknown';
  if (/AppleWebKit/i.test(ua)) engine = 'Blink/WebKit';
  if (/Gecko\//i.test(ua) && isFirefox) engine = 'Gecko';
  if (isSamsungInternet) engine = 'Blink (Samsung)';

  let os = 'Unknown';
  if (/Android/i.test(ua)) os = `Android ${matchVersion(ua, /Android ([\d.]+)/i)}`;
  else if (/iPhone|iPad|iPod/i.test(ua))
    os = `iOS ${matchVersion(ua, /OS ([\d_]+)/i).replace(/_/g, '.')}`;
  else if (/Mac OS X/i.test(ua))
    os = `macOS ${matchVersion(ua, /Mac OS X ([\d_]+)/i).replace(/_/g, '.')}`;
  else if (/Windows/i.test(ua)) os = 'Windows';
  else if (/Linux/i.test(ua)) os = 'Linux';

  return {
    browser,
    version,
    engine,
    os,
    isSamsungInternet,
    isChrome,
    isFirefox,
    isSafari,
    userAgent: ua,
  };
}

function matchVersion(ua: string, re: RegExp): string {
  const m = ua.match(re);
  return m?.[1] ?? '';
}

/**
 * Build an Android intent URL to open the current page in Chrome.
 * Returns null when not on Android or URL is unavailable.
 */
export function chromeIntentUrl(href: string = typeof location !== 'undefined' ? location.href : ''): string | null {
  if (typeof navigator === 'undefined') return null;
  if (!/Android/i.test(navigator.userAgent)) return null;
  if (!href) return null;
  try {
    const u = new URL(href);
    // intent://host/path#Intent;scheme=http;package=com.android.chrome;end
    const path = `${u.host}${u.pathname}${u.search}${u.hash}`;
    return `intent://${path}#Intent;scheme=${u.protocol.replace(':', '')};package=com.android.chrome;end`;
  } catch {
    return null;
  }
}
